import { Input, Table, Tabs, Tooltip } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import jsPDF from 'jspdf'
import { Download, FileText, Plane, Receipt, Search, Ship, Wallet } from 'lucide-react'
import { getcreditlimitbymode, getcreditlist } from '../../store/User/CreditLimitSlice'
import { formatrates } from '../../functions/Utils'
import TablePagination from '../common/TablePagination'
import './CreditInvoices.css'

const PAGE_SIZE = 10

const MODES = {
    air: { label: 'Air Cargo', Icon: Plane },
    ocean: { label: 'Ocean Cargo', Icon: Ship },
}

/**
 * Builds and saves a one-page PDF for a credit_list row. credit_list carries
 * no file URL, so the invoice is generated from the row itself.
 */
const downloadInvoice = (invoice) => {
    const pdf = new jsPDF('p', 'mm', 'a4')
    // jsPDF's built-in fonts have no ₹ glyph, so amounts go out as "INR".
    const amount = `INR ${Number(invoice?.invoice_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    pdf.setFontSize(18)
    pdf.text('Invoice', 20, 25)
    pdf.setFontSize(11)
    const rows = [
        ['Invoice No', invoice?.invoice_no],
        ['Invoice Date', dayjs(invoice?.invoice_date).format('DD MMM YYYY')],
        ['Invoice Type', invoice?.invoice_type],
        ['Invoice Value', amount],
    ]
    rows.forEach(([label, value], i) => {
        pdf.text(`${label}:`, 20, 45 + i * 10)
        pdf.text(String(value ?? '-'), 70, 45 + i * 10)
    })
    pdf.save(`${invoice?.invoice_no || 'invoice'}.pdf`)
}

const CreditInvoices = () => {
    const dispatch = useDispatch()
    const userid = useSelector((state) => state.data?.data?.data?.user?.id)
    const [loading, setLoading] = useState(true)
    const [invoices, setInvoices] = useState([])
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: PAGE_SIZE })
    const [searchText, setSearchText] = useState('')
    const [mode, setMode] = useState('air')
    const [creditLimit, setCreditLimit] = useState(null)

    const fetchInvoices = (page, limit) => {
        setLoading(true)
        dispatch(getcreditlist({ mode, page, limit }))
        .then(res=>{
            const body = res?.payload?.data
            setInvoices(body?.data || [])
            if (body?.pagination) setPagination(body.pagination)
            setLoading(false)
        })
        .catch(err=>{
            console.error(err);
            setLoading(false)
        })
    }

    // Reload from page 1 whenever the Air/Ocean tab changes.
    useEffect(()=>{
        setSearchText('')
        fetchInvoices(1, pagination.limit)
    },[mode])

    // Credit limit for the selected mode, shown in the summary cards.
    useEffect(()=>{
        if (!userid) return
        setCreditLimit(null)
        dispatch(getcreditlimitbymode({ mode, userid }))
        .then(res=>{
            if (res?.payload?.data?.statusCode === 200) {
                setCreditLimit(res?.payload?.data?.data?.creditlimit ?? 0)
            }
        })
    },[mode, userid])

    // credit_list has no search parameter, so search filters the loaded page.
    const filteredInvoices = useMemo(()=>{
        const term = searchText.trim().toLowerCase()
        if (!term) return invoices
        return invoices.filter((row)=>
            [row?.invoice_no, row?.invoice_type, row?.invoice_value, row?.invoice_date, dayjs(row?.invoice_date).format('DD MMM YYYY')]
                .some((value)=> String(value ?? '').toLowerCase().includes(term))
        )
    },[invoices, searchText])

    const pageValue = useMemo(()=>
        invoices.reduce((sum, row)=> sum + Number(row?.invoice_value || 0), 0)
    ,[invoices])

    const columns = [
        {
            title: 'S.No',
            key: 'slno',
            align: 'center',
            width: 80,
            render: (text, record, index)=> (pagination.page - 1) * pagination.limit + index + 1
        },
        {
            title: 'Invoice No',
            dataIndex: 'invoice_no',
            key: 'invoice_no',
            align: 'center',
            render: (value)=> <span className='ci-invoice-no'>{value || '-'}</span>
        },
        {
            title: 'Invoice Type',
            dataIndex: 'invoice_type',
            key: 'invoice_type',
            align: 'center',
            render: (value)=> value ? <span className='ci-badge air'>{value}</span> : '-'
        },
        {
            title: 'Invoice Date',
            dataIndex: 'invoice_date',
            key: 'invoice_date',
            align: 'center',
            render: (value)=> value ? dayjs(value).format('DD/MM/YYYY') : '-'
        },
        {
            title: 'Invoice Value',
            dataIndex: 'invoice_value',
            key: 'invoice_value',
            align: 'center',
            render: (value)=> <span className='ci-amount'>{formatrates(Number(value || 0).toFixed(2))}</span>
        },
        {
            title: 'Actions',
            key: 'action',
            align: 'center',
            width: 110,
            render: (datarow)=> (
                <Tooltip title='Download Invoice'>
                    <button
                        type='button'
                        aria-label={`Download ${datarow?.invoice_no || 'invoice'}`}
                        onClick={()=> downloadInvoice(datarow)}
                        className='ci-download-btn'
                    >
                        <Download size={15} />
                    </button>
                </Tooltip>
            )
        },
    ]

    const fromIdx = (pagination.page - 1) * pagination.limit + 1
    const toIdx = Math.min(pagination.page * pagination.limit, pagination.total)

    return (
        <div className="sm:p-3 p-5 w-full rounded-2xl shadow-cardsection bg-white">
            <div className='ci-header'>
                <div className='ci-title-wrap'>
                    <span className='ci-title-icon'><Receipt size={20} /></span>
                    <div>
                        <h1 className='ci-title'>Credit Invoices</h1>
                        <p className='ci-subtitle'>View and download invoices billed against your credit limit</p>
                    </div>
                </div>
            </div>

            <div className='ci-stats'>
                <div className='ci-stat highlight'>
                    <span className='ci-stat-icon'><Wallet size={20} /></span>
                    <div>
                        <div className='ci-stat-label'>{MODES[mode].label} Credit Limit</div>
                        <div className='ci-stat-value'>{creditLimit === null ? '—' : formatrates(Number(creditLimit).toFixed(2))}</div>
                    </div>
                </div>
                <div className='ci-stat'>
                    <span className='ci-stat-icon'><FileText size={20} /></span>
                    <div>
                        <div className='ci-stat-label'>Total Invoices</div>
                        <div className='ci-stat-value'>{loading ? '—' : pagination.total.toLocaleString()}</div>
                    </div>
                </div>
                <div className='ci-stat'>
                    <span className='ci-stat-icon'><Receipt size={20} /></span>
                    <div>
                        <div className='ci-stat-label'>Invoice Value</div>
                        <div className='ci-stat-value'>{loading ? '—' : formatrates(pageValue.toFixed(2))}</div>
                        <div className='ci-stat-hint'>Invoices on this page</div>
                    </div>
                </div>
            </div>

            <Tabs
                activeKey={mode}
                type="card"
                items={Object.entries(MODES).map(([key, { label, Icon }])=> ({
                    key,
                    label: <span className='ci-tab-label'><Icon size={15} />{label}</span>,
                }))}
                onChange={setMode}
            />

            <div className="ci-toolbar">
                <span className='ci-count'>
                    {pagination.total > 0
                        ? <>Showing <b>{fromIdx}–{toIdx}</b> of <b>{pagination.total.toLocaleString()}</b> invoices</>
                        : 'No invoices'}
                </span>
                <Input
                    allowClear
                    prefix={<Search size={15} color='#a8a29e' />}
                    placeholder='Search by invoice no, invoice type, date or value'
                    value={searchText}
                    onChange={(e)=> setSearchText(e.target.value)}
                    className='ci-search'
                />
            </div>

            <div className="tableResponsiveASAP ci-table-wrap">
                <Table
                className="bookingHistoryTable"
                rowKey={(record) => record?._id}
                columns={columns}
                dataSource={filteredInvoices}
                scroll={{x:768}}
                loading={loading}
                pagination={false}
                locale={{
                    emptyText: (
                        <div className='ci-empty'>
                            <span className='ci-empty-icon'><Receipt size={24} /></span>
                            <span className='ci-empty-title'>
                                {searchText ? 'No matching invoices' : `No ${MODES[mode].label.toLowerCase()} invoices yet`}
                            </span>
                            <span className='ci-empty-text'>
                                {searchText ? 'Try a different invoice number, date or amount.' : 'Invoices billed on credit will show up here.'}
                            </span>
                        </div>
                    )
                }}
                />
                <TablePagination
                    page={pagination.page}
                    pageSize={pagination.limit}
                    total={pagination.total}
                    itemLabel='invoices'
                    onChange={fetchInvoices}
                />

            </div>
        </div>
    )
}

export default React.memo(CreditInvoices)
